<template>
    <div style="min-height: 100vh;">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center flex-wrap mb-4" style="gap: 12px;">
            <div>
                <h4 class="ctc-dashboard-title mb-1">CTC Upload Audits</h4>
                <div class="ctc-dashboard-subtitle">Snapshots of CTC heads, rules, and attributes captured during payment uploads.</div>
            </div>
            <!-- Search comes from <common-list> below, so no separate box here. -->
        </div>

        <!-- Audit Table -->
        <div class="card shadow-sm border-0 ctc-rounded-lg mb-4">
            <div class="card-body p-0">
                <div class="p-4 border-bottom bg-white rounded-top">
                    <h6 class="font-weight-bold text-dark mb-0">Audit Log Entries</h6>
                </div>
                <!-- Rendered by the shared <common-list> (DemoList.vue), which is
                     left unmodified. Every column sits in appended_column so the
                     list renders plain cells rather than its avatar-prefixed name
                     cell. The design asks for a capsule instead of DemoList's
                     three-dot row menu, so the action is an ordinary last column
                     whose cell text is styled as a pill below — clicking it emits
                     the same row event the menu would have. -->
                <div class="ctc-audit-list-wrap bg-white rounded-bottom">
                    <common-list
                        :key="`ctc-audits-${auditListKey}`"
                        :employees="auditRows"
                        :default_column="emptyColumns"
                        :appended_column="auditColumns"
                        :company_options="emptyColumns"
                        primary_key="id"
                        v-on:childToParent="onAuditListEvent"
                    ></common-list>
                </div>
            </div>
        </div>

        <!-- Details Modal.
             Built with the modal chrome hidden and the frame drawn here instead:
             the design puts the title flush left at 32px over a hairline rule with
             its own close glyph pinned to the corner, none of which BootstrapVue's
             default header gives. The same treatment the Upload Payment Data modal
             on the Annual Reports screen uses. -->
        <b-modal id="modal-audit-details" size="xl" centered hide-header hide-footer modal-class="ctc-audit-modal">
            <div v-if="selectedAudit" class="ctc-audit-snapshot">

                <!-- Title -->
                <div class="ctc-audit-titlebar">
                    <h5 class="ctc-audit-title">Audit Snapshot Details</h5>
                    <button type="button" class="ctc-audit-x" aria-label="Close"
                        @click="$bvModal.hide('modal-audit-details')">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <!-- Upload context / head information -->
                <div class="ctc-audit-context">
                    <div class="ctc-audit-context-card">
                        <div class="ctc-audit-context-label">UPLOAD CONTEXT</div>
                        <div class="ctc-audit-context-line">
                            <span class="ctc-audit-context-key">Year:</span>
                            <span class="ctc-audit-context-value">{{ selectedAudit.year }}</span>
                        </div>
                        <div class="ctc-audit-context-line">
                            <span class="ctc-audit-context-key">Timestamp:</span>
                            <span class="ctc-audit-context-value">{{ formatDate(selectedAudit.created_at) }}</span>
                        </div>
                    </div>
                    <div class="ctc-audit-context-card">
                        <div class="ctc-audit-context-label">HEAD INFORMATION</div>
                        <div class="ctc-audit-context-line">
                            <span class="ctc-audit-context-key">Name:</span>
                            <span class="ctc-audit-context-value">{{ selectedAudit.head_name }}</span>
                            <span class="ctc-audit-annotation" v-if="headNameNow">(now: {{ headNameNow }})</span>
                            <span class="ctc-audit-annotation" v-if="headDeleted">(Head Deleted)</span>
                        </div>
                        <div class="ctc-audit-context-line">
                            <span class="ctc-audit-context-key">Type:</span>
                            <span class="ctc-audit-context-value">{{ headType }}</span>
                        </div>
                    </div>
                </div>

                <!-- The whole modal contrasts the snapshot against the head's present
                     configuration, so the convention is stated once, up front. -->
                <p class="ctc-audit-note" v-if="!headDeleted">
                    Values shown are those captured at upload time. Where the head has been
                    edited since, the <span class="ctc-audit-annotation">current value appears in brackets</span>.
                </p>
                <p class="ctc-audit-note ctc-audit-note-warn" v-else>
                    This CTC head has been deleted since the upload, so the snapshot below cannot be
                    compared against a current configuration.
                </p>

                <b-tabs class="ctc-audit-tabs" nav-class="ctc-audit-tabnav" content-class="ctc-audit-tabpanel">
                    <b-tab active>
                        <template v-slot:title>
                            Head Configuration
                            <span class="ctc-audit-tabcount" v-if="changedHeadFields.length">{{ changedHeadFields.length }}</span>
                        </template>
                        <h6 class="ctc-audit-section-title">Head Configuration Snapshot</h6>
                        <div class="ctc-audit-table-wrap">
                            <table class="table mb-0 ctc-audit-table ctc-audit-table-fields">
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        <th>Value at Snapshot <span class="ctc-audit-th-hint">(current)</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="field in headFields" :key="field.key" :class="{ 'ctc-audit-changed': field.changed }">
                                        <td>{{ field.label }}</td>
                                        <td>
                                            <!-- Conditional rules pay per employee and leave the head's own
                                                 amount behind as a figure nobody was ever paid, which is why
                                                 the add/edit form drops the Amount field while rules are on.
                                                 The snapshot does the same: no figure, just the reason. Rules
                                                 switched on *after* this upload are a different matter — the
                                                 amount below was paid, so it stays and the takeover is noted
                                                 in brackets instead. -->
                                            <span class="ctc-audit-rule-driven" v-if="field.ruleDriven">Amount coming from rules</span>
                                            <span v-else>{{ field.valueText }}</span>
                                            <span class="ctc-audit-annotation" v-if="field.changed"><template v-if="field.currentRuleDriven">(Amount coming from rules)</template><template v-else>({{ field.currentText }})</template></span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </b-tab>
                    <b-tab>
                        <template v-slot:title>
                            Rules (Custom Attributes)
                            <span class="ctc-audit-tabcount" v-if="changedRuleCount">{{ changedRuleCount }}</span>
                        </template>
                        <div v-if="flattenedRuleTree.length > 0 || addedRules.length > 0" class="ctc-audit-table-wrap">
                            <table class="table mb-0 ctc-audit-table ctc-audit-table-rules">
                                <thead>
                                    <tr>
                                        <th>Rule Tree</th>
                                        <th>Amount</th>
                                        <th>Level</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(rule, i) in flattenedRuleTree" :key="'snap-' + i" :class="{ 'ctc-audit-changed': rule.amountChanged || rule.removed }">
                                        <td :style="{ paddingLeft: (10 + (rule.level - 1) * 20) + 'px' }">
                                            <b-icon icon="arrow-return-right" class="ctc-audit-branch" v-if="rule.level > 1"></b-icon>
                                            <!-- Attribute Display -->
                                            <span class="ctc-audit-strong">{{ resolveAttribute(rule).main }}</span>
                                            <span class="ctc-audit-annotation" v-if="resolveAttribute(rule).sub">({{ resolveAttribute(rule).sub }})</span>
                                            <span class="ctc-audit-annotation" v-if="resolveAttribute(rule).deleted">(Currently Not Available)</span>
                                            <span class="ctc-audit-sep">:</span>
                                            <!-- Option Display -->
                                            <span>{{ resolveOption(rule).main }}</span>
                                            <span class="ctc-audit-annotation" v-if="resolveOption(rule).sub">({{ resolveOption(rule).sub }})</span>
                                            <span class="ctc-audit-annotation" v-if="resolveOption(rule).deleted">(Currently Not Available)</span>
                                            <!-- Rules are wiped and re-inserted on every head save, so a rule
                                                 is matched to its current counterpart by attribute/option path. -->
                                            <span class="ctc-audit-flag ctc-audit-flag-removed" v-if="rule.removed">Removed since upload</span>
                                        </td>
                                        <td class="ctc-audit-amount">
                                            ৳{{ rule.amount }}
                                            <span class="ctc-audit-annotation" v-if="rule.amountChanged">(৳{{ rule.currentAmount }})</span>
                                        </td>
                                        <td class="text-center"><span class="ctc-audit-level">{{ rule.level }}</span></td>
                                    </tr>
                                    <tr v-for="(rule, i) in addedRules" :key="'added-' + i" class="ctc-audit-changed">
                                        <td :style="{ paddingLeft: (10 + (rule.level - 1) * 20) + 'px' }">
                                            <b-icon icon="arrow-return-right" class="ctc-audit-branch" v-if="rule.level > 1"></b-icon>
                                            <span class="ctc-audit-strong">{{ rule.attribute_name || 'N/A' }}</span>
                                            <span class="ctc-audit-sep">:</span>
                                            <span>{{ rule.option_name || 'N/A' }}</span>
                                            <span class="ctc-audit-flag ctc-audit-flag-added">Added after upload</span>
                                        </td>
                                        <td class="ctc-audit-amount">৳{{ rule.amount }}</td>
                                        <td class="text-center"><span class="ctc-audit-level">{{ rule.level }}</span></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else class="ctc-audit-empty">
                            No rules configured for this head at the time of upload.
                        </div>
                    </b-tab>
                    <b-tab title="Employee Attribute Map">
                        <div class="ctc-audit-toolbar">
                            <span class="ctc-audit-caption">Snapshot of employee attribute selections at the time of upload.</span>
                            <div class="input-group search-input-group ctc-audit-search">
                                <div class="input-group-prepend">
                                    <span class="input-group-text">
                                        <b-icon icon="search" class="search-icon"></b-icon>
                                    </span>
                                </div>
                                <input type="text" class="form-control"
                                    placeholder="Search by name or code..."
                                    v-model="empSearchQuery">
                            </div>
                        </div>
                        <div class="ctc-audit-table-wrap ctc-audit-table-scroll">
                            <table class="table mb-0 ctc-audit-table ctc-audit-table-employees">
                                <thead>
                                    <tr>
                                        <th>Employee</th>
                                        <th>Attribute</th>
                                        <th>Option Selected</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(map, i) in filteredEmpAttributes" :key="i">
                                        <td>
                                            <div class="ctc-audit-emp-name">{{ map.employee_name || 'N/A' }}</div>
                                            <div class="ctc-audit-emp-code">{{ map.employee_code || map.employee_id }}</div>
                                        </td>
                                        <td>
                                            <span>{{ resolveAttribute(map).main }}</span>
                                            <span class="ctc-audit-annotation" v-if="resolveAttribute(map).sub">({{ resolveAttribute(map).sub }})</span>
                                            <span class="ctc-audit-annotation" v-if="resolveAttribute(map).deleted">(Not Available)</span>
                                        </td>
                                        <td>
                                            <span>{{ resolveOption(map).main }}</span>
                                            <span class="ctc-audit-annotation" v-if="resolveOption(map).sub">({{ resolveOption(map).sub }})</span>
                                            <span class="ctc-audit-annotation" v-if="resolveOption(map).deleted">(Not Available)</span>
                                        </td>
                                    </tr>
                                    <tr v-if="!filteredEmpAttributes.length">
                                        <td colspan="3" class="ctc-audit-empty-cell">No employee attribute selections were captured for this upload.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </b-tab>
                </b-tabs>

                <div class="ctc-audit-footer">
                    <button type="button" class="ctc-audit-close-btn" @click="$bvModal.hide('modal-audit-details')">Close</button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios';
import ctcOverlay from '../js/ctcOverlay';

// The add/edit form's own wording for the head's enum columns, so the audit reads
// the way the screen those values were entered on does rather than echoing the
// stored keys. Values outside these lists (an older head, a column extended
// since) fall back to a generic title-cased rendering.
const HEAD_VALUE_LABELS = {
    type:              { compensation: 'Compensation', annual: 'Annual' },
    status:            { active: 'Active', inactive: 'Inactive' },
    category:          { main_part: 'Main Part', other_part: 'Other Part' },
    payment_frequency: { yearly: 'Yearly', half_yearly: 'Half-Yearly', quarterly: 'Quarterly', monthly: 'Monthly' },
};

// Payment timing reads as a sentence about the payment period, so its label
// depends on the head's frequency — the same three options the form offers, worded
// against whichever period applies.
const PAYMENT_PERIODS = {
    yearly: 'Year',
    half_yearly: 'Half-Year',
    quarterly: 'Quarter',
    monthly: 'Month',
};

export default {
    mixins: [ctcOverlay],
    name: 'AuditReport',
    watch: {
        loading(newVal) {
            this.ctcOverlay(newVal);
        }
    },
    data() {
        return {
            audits: [],
            searchQuery: '',
            selectedAudit: null,
            resolvedNames: {
                attributes: {},
                options: {}
            },
            // The head and its rules as they stand right now. Everything in the
            // snapshot modal is compared against these, so an admin can see what
            // was edited after the payment upload. `currentHead` stays null when
            // the head has since been deleted.
            currentHead: null,
            currentRules: [],
            empSearchQuery: '',
            loading: false,
            // <common-list> config: shared frozen empty array, and a key bumped
            // when rows are reloaded so the list re-initialises cleanly.
            emptyColumns: [],
            auditListKey: 0,
            // Fields the snapshot either carries for the module's own bookkeeping or
            // no longer tracks at all. New snapshots stop capturing the untracked
            // ones (see CtcAuditController::stripUntrackedFields), but audits taken
            // before that still hold them, so they are filtered here as well.
            excludedKeys: [
                'id', 'company_id', 'created_at', 'deleted_at', 'sort_order',
                'updated_at', 'rule_config', 'is_texable', 'is_taxable',
                'is_mendatory', 'is_mandatory', 'percentage_of', 'percentage_value',
                'is_gf', 'formula', 'is_automated', 'allowed_other_part', 'calculation_type'
            ]
        }
    },
    computed: {
        // Columns for <common-list>. All appended (never default_column) so no
        // avatar cell is rendered; appended columns always get their own header.
        auditColumns() {
            return [
                { value: 'year',        text: 'Year',        type: 'text' },
                { value: 'head_name',   text: 'CTC Head',    type: 'text' },
                { value: 'uploaded_at', text: 'Uploaded At', type: 'text' },
                { value: 'action_label', text: 'Action',     type: 'text' },
            ];
        },
        // DemoList prints raw values, so the date is formatted up front, and the
        // capsule's caption is carried as an ordinary cell value.
        auditRows() {
            return (this.audits || []).map(audit => ({
                ...audit,
                // DemoList renders plain text, so the rename/delete marker is baked
                // into the cell rather than styled like it is inside the modal.
                head_name: this.headNameCell(audit),
                uploaded_at: this.formatDate(audit.created_at),
                action_label: 'View Snapshot',
            }));
        },
        headDeleted() {
            return !!this.selectedAudit && !this.currentHead;
        },
        // Head type as recorded in the snapshot — shown in the Head Information card.
        headType() {
            const snapshot = (this.selectedAudit && this.selectedAudit.head_snapshot) || null;
            if (!snapshot || !snapshot.type) return 'N/A';
            return this.headValueText('type', snapshot.type, null);
        },
        // Current head name, but only when it differs from the snapshot — the
        // template treats a falsy value as "nothing to show in brackets".
        headNameNow() {
            if (!this.currentHead || !this.selectedAudit) return null;
            return this.currentHead.name !== this.selectedAudit.head_name ? this.currentHead.name : null;
        },
        // Whether the amount was driven by the conditional rule builder at the time
        // of the upload, and whether it is now. The two are tracked separately
        // because rules turned on after an upload must not hide the figure the
        // employees were actually paid.
        snapshotRuleDriven() {
            const snapshot = (this.selectedAudit && this.selectedAudit.head_snapshot) || null;
            return this.isRuleDriven(snapshot, (this.selectedAudit && this.selectedAudit.rules_snapshot) || []);
        },
        currentRuleDriven() {
            return this.isRuleDriven(this.currentHead, this.currentRules);
        },
        // One row per snapshot field, each carrying the head's present value so the
        // template can print it in brackets when the two differ.
        headFields() {
            const snapshot = (this.selectedAudit && this.selectedAudit.head_snapshot) || {};
            // Payment timing is worded against the head's payment frequency, and the
            // two sides of the row can sit on different frequencies, so each side is
            // labelled with its own.
            const snapshotFrequency = snapshot.payment_frequency || null;
            const currentFrequency = this.currentHead ? this.currentHead.payment_frequency : null;
            return Object.keys(snapshot)
                .filter(key => !this.isExcluded(key))
                .map(key => {
                    const value = snapshot[key];
                    // A field the current head no longer has (dropped column) can't
                    // be compared, so it is reported as unchanged rather than as a
                    // change to an empty value.
                    const comparable = !!this.currentHead && Object.prototype.hasOwnProperty.call(this.currentHead, key);
                    const currentValue = comparable ? this.currentHead[key] : null;
                    // The head keeps its last fixed amount on the row even while rules
                    // are running the head, so for this one field the stored figures
                    // say nothing about whether anything changed — where the amount
                    // came from does.
                    const ruleDriven = key === 'fixed_amount' && this.snapshotRuleDriven;
                    const currentRuleDriven = key === 'fixed_amount' && comparable && this.currentRuleDriven;
                    const changed = (ruleDriven || currentRuleDriven)
                        ? (comparable && ruleDriven !== currentRuleDriven)
                        : (comparable && this.comparableValue(value) !== this.comparableValue(currentValue));
                    return {
                        key,
                        label: this.humanize(key),
                        value,
                        currentValue,
                        // Changes are still decided on the stored values — only what
                        // the reader sees is reworded.
                        valueText: this.headValueText(key, value, snapshotFrequency),
                        currentText: this.headValueText(key, currentValue, currentFrequency),
                        changed,
                        ruleDriven,
                        currentRuleDriven,
                    };
                });
        },
        changedHeadFields() {
            return this.headFields.filter(f => f.changed);
        },
        filteredEmpAttributes() {
            if (!this.selectedAudit || !this.selectedAudit.employee_attributes_snapshot) return [];
            if (!this.empSearchQuery) return this.selectedAudit.employee_attributes_snapshot;
            const q = this.empSearchQuery.toLowerCase();
            return this.selectedAudit.employee_attributes_snapshot.filter(m =>
                (m.employee_name && m.employee_name.toLowerCase().includes(q)) ||
                (m.employee_code && m.employee_code.toLowerCase().includes(q)) ||
                String(m.employee_id).includes(q)
            );
        },
        flattenedRuleTree() {
            if (!this.selectedAudit || !this.selectedAudit.rules_snapshot) return [];

            const rules = this.flattenRules(this.selectedAudit.rules_snapshot);
            const currentByPath = this.currentRulesByPath;

            // Nothing to compare against once the head is gone — every rule went
            // with it, and flagging each one as "removed" would just be noise.
            if (this.headDeleted) return rules;

            return rules.map(rule => {
                const current = currentByPath[rule.path_key];
                if (!current) {
                    return { ...rule, removed: true, amountChanged: false };
                }
                return {
                    ...rule,
                    removed: false,
                    amountChanged: this.comparableValue(rule.amount) !== this.comparableValue(current.amount),
                    currentAmount: current.amount,
                };
            });
        },
        currentRulesByPath() {
            const map = {};
            this.flattenRules(this.currentRules).forEach(rule => {
                map[rule.path_key] = rule;
            });
            return map;
        },
        // Rules configured on the head after this upload took place. They have no
        // snapshot counterpart, so they are listed after the snapshot rows.
        addedRules() {
            if (this.headDeleted) return [];
            const snapshotPaths = {};
            this.flattenRules((this.selectedAudit && this.selectedAudit.rules_snapshot) || [])
                .forEach(rule => { snapshotPaths[rule.path_key] = true; });

            return this.flattenRules(this.currentRules).filter(rule => !snapshotPaths[rule.path_key]);
        },
        changedRuleCount() {
            return this.flattenedRuleTree.filter(r => r.removed || r.amountChanged).length + this.addedRules.length;
        }
    },
    mounted() {
        this.loadAudits();
    },
    methods: {
        loadAudits() {
            this.loading = true;
            axios.get('/cost-to-company/audits')
                .then(response => {
                    this.audits = response.data.data || [];
                    this.auditListKey++;
                })
                .catch(error => {
                    console.error('Error loading audits:', error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        // <common-list> reports every cell click here — the capsule included, since
        // it is a cell — and the snapshot is the row's only action.
        onAuditListEvent(value) {
            const id = value && value['id'];
            if (id) this.viewDetails(id);
        },
        viewDetails(id) {
            axios.get(`/cost-to-company/audits/${id}`)
                .then(response => {
                    const current = response.data.current || {};
                    this.selectedAudit = response.data.audit;
                    this.resolvedNames = response.data.resolved || { attributes: {}, options: {} };
                    this.currentHead = current.head || null;
                    this.currentRules = current.rules || [];
                    // Each snapshot opens with an unfiltered attribute map.
                    this.empSearchQuery = '';
                    this.$bvModal.show('modal-audit-details');
                })
                .catch(error => {
                    console.error('Error loading audit details:', error);
                });
        },
        // List cell for the CTC Head column: the name recorded at upload, plus the
        // head's present name whenever it has been renamed or deleted since.
        headNameCell(audit) {
            if (!audit.current_head_name) return `${audit.head_name} (deleted)`;
            if (audit.current_head_name !== audit.head_name) return `${audit.head_name} (now: ${audit.current_head_name})`;
            return audit.head_name;
        },
        // Snapshots are stored as JSON while the live head comes back from Eloquent,
        // so '' / null, 1 / true and '2596.00' / 2596 have to compare equal or every
        // field would look like it had changed.
        comparableValue(val) {
            if (val === null || val === undefined) return '';
            if (typeof val === 'boolean') return val ? '1' : '0';
            if (typeof val === 'object') return JSON.stringify(val);
            const str = String(val).trim();
            if (str !== '' && !isNaN(str)) return String(parseFloat(str));
            return str;
        },
        // Flattens a rule set into display order and stamps each rule with the
        // attribute/option path that identifies it. Head saves delete and re-insert
        // every rule row, so the ids differ between a snapshot and the live head —
        // the path is what stays the same.
        flattenRules(rules) {
            const source = JSON.parse(JSON.stringify(rules || []));
            if (!source.length) return [];

            const byId = {};
            source.forEach(rule => { byId[rule.id] = rule; });

            const pathKey = (rule) => {
                const parts = [];
                let node = rule;
                // Guarded against a corrupt parent chain looping forever.
                for (let depth = 0; node && depth < 20; depth++) {
                    parts.unshift(`${node.custom_attribute_id}:${node.option_id}`);
                    node = (node.parent_id === null || node.parent_id === undefined) ? null : byId[node.parent_id];
                }
                return parts.join('>');
            };

            const ordered = [];
            const buildTree = (parentId = null) => {
                source.filter(r => r.parent_id == parentId).forEach(child => {
                    ordered.push(child);
                    buildTree(child.id);
                });
            };
            buildTree(null);

            // Flat rule sets saved without a root (parent_id never null) still need
            // to render, so fall back to the raw list ordered by level.
            const list = ordered.length ? ordered : source.slice().sort((a, b) => a.level - b.level);

            return list.map(rule => ({ ...rule, path_key: pathKey(rule) }));
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        formatSnapshotValue(val) {
            if (val === null || val === undefined) return 'N/A';
            // Lists (the head's departments) read better joined than as raw JSON.
            if (Array.isArray(val)) return val.length ? val.join(', ') : 'None';
            if (typeof val === 'object') {
                return JSON.stringify(val);
            }
            return val;
        },
        // A head's amount comes from the rule builder only when a tree is configured
        // *and* there are rule rows behind it — a tree with no rows falls back to the
        // fixed amount (CtcReportController does the same), and that fixed amount is
        // then the figure employees were paid, so it must stay on screen.
        isRuleDriven(head, rules) {
            if (!head) return false;
            let config = head.rule_config;
            // The column is cast to array but holds a JSON-encoded string, so the
            // cast can hand back a string that needs one more decode — the same
            // dance CtcHeadController does before reading `field`.
            for (let pass = 0; pass < 2 && typeof config === 'string'; pass++) {
                try {
                    config = JSON.parse(config);
                } catch (e) {
                    return false;
                }
            }
            if (!config || typeof config !== 'object' || !config.field) return false;
            return Array.isArray(rules) && rules.length > 0;
        },
        isExcluded(key) {
            return this.excludedKeys.includes(key);
        },
        // How one head field is printed. Enum columns get the form's wording; the
        // head's name and its department list are text somebody typed and are left
        // exactly as they were entered.
        headValueText(key, val, frequency) {
            if (key === 'payment_timing') {
                return val ? this.paymentTimingLabel(val, frequency) : 'N/A';
            }
            const labels = HEAD_VALUE_LABELS[key];
            if (labels) {
                if (val === null || val === undefined || val === '') return 'N/A';
                return labels[val] || this.humanize(val);
            }
            return this.formatSnapshotValue(val);
        },
        paymentTimingLabel(timing, frequency) {
            const period = PAYMENT_PERIODS[frequency] || PAYMENT_PERIODS.monthly;
            if (timing === 'month') return `Based On Current ${period}`;
            if (timing === 'date') return 'Based On Joining Date';
            if (timing === 'next_month') return `Effective From Next ${period}`;
            return this.humanize(timing);
        },
        // Column names and unmapped stored values are both keys rather than prose,
        // so one routine words them: separators become spaces, camel case is split,
        // and every word is capitalised.
        humanize(key) {
            return String(key)
                .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                .replace(/[_-]+/g, ' ')
                .trim()
                .replace(/\s+/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
        },
        resolveAttribute(item) {
            const historical = item.attribute_name;
            const current = this.resolvedNames.attributes[item.custom_attribute_id];
            return this.getHistoricalObject(historical, current);
        },
        resolveOption(item) {
            const historical = item.option_name;
            const current = this.resolvedNames.options[item.option_id];
            return this.getHistoricalObject(historical, current);
        },
        getHistoricalObject(historical, current) {
            const res = { main: historical || current || 'N/A', sub: null, deleted: false };

            if (!historical) {
                if (!current) res.deleted = true;
                return res;
            }

            if (!current) {
                res.deleted = true;
                return res;
            }

            if (historical !== current) {
                res.sub = current;
            }

            return res;
        }
    }
}
</script>

<style scoped>
/* ---------------------------------------------------------------------------
   Audit Snapshot Details modal. Every size, weight and colour below comes from
   the Figma CSS export of the three "Audit Snapshot Details" frames
   (audit_head_configuration / audit_rule / audit_employee) rather than being
   eyeballed, so the values can be diffed against the export a line at a time.
   The frame is 987px wide with 20px padding and a 30px gap between its blocks.
   --------------------------------------------------------------------------- */
.ctc-audit-snapshot {
    font-family: 'Poppins', sans-serif;
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding: 20px;
}

/* ── Title ──────────────────────────────────────────────────────────────── */
.ctc-audit-titlebar {
    position: relative;
    padding-bottom: 13px;
    border-bottom: 1px solid #BDDBCE;
}

.ctc-audit-title {
    font-weight: 600;
    font-size: 32px;
    line-height: 48px;
    letter-spacing: -0.02em;
    color: #212529;
    margin: 0;
}

/* The frame pins its close glyph to the top-right corner of the padding box
   rather than sharing the title's row. Same cross as the Upload Payment Data
   modal on the Annual Reports screen (.ctc-modal-close): the native × glyph at
   1.15rem/#333, half-opacity until hover, instead of a b-icon. */
.ctc-audit-x {
    position: absolute;
    top: 0;
    right: 0;
    margin: 0;
    padding: 0;
    background: transparent;
    border: none;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1;
    color: #333;
    text-shadow: 0 1px 0 #fff;
    opacity: 0.5;
    outline: none;
    cursor: pointer;
    transition: opacity 0.2s;
}

.ctc-audit-x:hover {
    opacity: 0.8;
}

/* ── Upload context / head information cards ────────────────────────────── */
.ctc-audit-context {
    display: flex;
    gap: 50px;
}

.ctc-audit-context-card {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    padding: 15px 15px 15px 20px;
    background: #FFFFFF;
    border-radius: 16px;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
}

.ctc-audit-context-label {
    font-weight: 600;
    font-size: 13px;
    line-height: 150%;
    color: #000000;
}

.ctc-audit-context-line {
    font-size: 13px;
    line-height: 150%;
    word-break: break-word;
}

.ctc-audit-context-key {
    font-weight: 500;
    color: #8F8F8F;
    margin-right: 4px;
}

.ctc-audit-context-value {
    font-weight: 600;
    color: #34444C;
}

/* ── Bracketed "current value" annotation ───────────────────────────────── */
/* One token for every bracketed aside in the modal — the head's present name,
   a renamed attribute or option, a changed amount, a value that no longer
   exists. The frames render them all in the same mint green. */
.ctc-audit-annotation {
    margin-left: 4px;
    font-weight: 500;
    font-size: 13px;
    color: #6AC49E;
}
/* Stands in for the amount whenever rules were driving the head, so it reads as
   a statement about the field rather than as a value. */
.ctc-audit-rule-driven {
    font-weight: 500;
    font-size: 13px;
    font-style: italic;
    color: #8F8F8F;
}

.ctc-audit-note {
    margin: -14px 0 0 0;
    font-weight: 400;
    font-size: 13px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: #727272;
}

/* The head is gone, so nothing on the page can be compared — that is worth more
   than the muted caption treatment the ordinary note gets. */
.ctc-audit-note-warn {
    padding: 10px 15px;
    margin-top: -14px;
    background: #FFF8E6;
    border: 1px solid #E2C3B5;
    border-radius: 4px;
    color: #9D5617;
}

/* ── Tab strip ──────────────────────────────────────────────────────────── */
/* BootstrapVue renders the nav itself, so it is reached with ::v-deep. The
   frame draws a joined segmented control: 40px tall cells sharing their
   borders, only the outer top corners rounded, the active cell filled.

   Every colour, border and radius below is !important because nncustom.scss
   claims `.nav-tabs .nav-link` / `.nav-link.active` app-wide with !important of
   its own — a #dee2e6 hairline, black text and a grey `rgba(128,128,128,0.2)`
   fill on the active cell. Without matching it the active tab renders grey
   instead of the frame's deep green, whatever the specificity here. */
.ctc-audit-tabs ::v-deep .ctc-audit-tabnav {
    border-bottom: none;
    flex-wrap: nowrap;
    overflow-x: auto;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-item {
    margin: 0;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 40px;
    padding: 10px;
    border: 1px solid #BDDBCE !important;
    border-radius: 0 !important;
    background-color: #FFFFFF !important;
    font-weight: 600;
    font-size: 13px;
    line-height: 150%;
    white-space: nowrap;
    color: #01524B !important;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-item + .nav-item .nav-link {
    border-left: none !important;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-item:first-child .nav-link {
    border-radius: 4px 0 0 0 !important;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-item:last-child .nav-link {
    border-radius: 0 4px 0 0 !important;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-link:hover {
    background-color: #F2F9F6 !important;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-link.active {
    background-color: #01524B !important;
    border-color: #01524B !important;
    color: #FFFFFF !important;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-link.active:hover {
    background-color: #01524B !important;
}

/* Count of fields/rules that no longer match the current configuration. Not in
   the frames — they show a head with nothing changed — so it is drawn to sit
   inside the 40px cell without altering it. */
.ctc-audit-tabcount {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #FFF3E1;
    color: #9D5617;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabnav .nav-link.active .ctc-audit-tabcount {
    background: #FFFFFF;
    color: #01524B;
}

.ctc-audit-tabs ::v-deep .ctc-audit-tabpanel {
    margin-top: 30px;
}

.ctc-audit-section-title {
    font-weight: 500;
    font-size: 20px;
    line-height: 30px;
    letter-spacing: -0.02em;
    color: #262626;
    margin: 0 0 20px 0;
}

/* ── Tables ─────────────────────────────────────────────────────────────── */
/* Same table as every other CTC screen: a 45px #01524B header divided by
   #126D66, over 40px body rows on a #E8E8E8 hairline grid. */
.ctc-audit-table-wrap {
    border-radius: 4px 4px 0 0;
    overflow: hidden;
}

.ctc-audit-table-scroll {
    max-height: 500px;
    overflow-y: auto;
}

.ctc-audit-table {
    margin-bottom: 0;
    table-layout: fixed;
}

.ctc-audit-table thead th {
    height: 45px;
    padding: 10px;
    background: #01524B;
    border: 0;
    font-weight: 600;
    font-size: 15px;
    line-height: 19px;
    letter-spacing: -0.04em;
    text-align: center;
    vertical-align: middle;
    color: #FFFFFF;
}

.ctc-audit-table thead th + th {
    border-left: 1px solid #126D66;
}

/* The employee map scrolls inside its own box, so its header has to stay put.
   A collapsed table drops the borders of a sticky cell once it detaches, so the
   divider is redrawn as an inset shadow, which survives. */
.ctc-audit-table-scroll .ctc-audit-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
}

.ctc-audit-table-scroll .ctc-audit-table thead th + th {
    border-left: 0;
    box-shadow: inset 1px 0 0 #126D66;
}

.ctc-audit-th-hint {
    font-weight: 400;
    color: #BDDBCE;
}

.ctc-audit-table tbody td {
    height: 40px;
    padding: 10px;
    border: 1px solid #E8E8E8;
    font-weight: 400;
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: #34444C;
    vertical-align: middle;
    word-break: break-word;
}

.ctc-audit-table-fields td:first-child,
.ctc-audit-table-fields th:first-child {
    width: 50%;
}

.ctc-audit-table-rules th:nth-child(2),
.ctc-audit-table-rules td:nth-child(2) {
    width: 22%;
}

.ctc-audit-table-rules th:last-child,
.ctc-audit-table-rules td:last-child {
    width: 12%;
}

.ctc-audit-table-employees th:first-child,
.ctc-audit-table-employees td:first-child {
    width: 32%;
}

/* A row whose value no longer matches the head's current configuration. The tint
   is deliberately faint — the bracketed current value is the actual signal. */
.ctc-audit-changed > td {
    background-color: #fff8e6;
}

.ctc-audit-strong {
    font-weight: 600;
}

.ctc-audit-sep {
    margin: 0 6px;
    color: #8F8F8F;
}

.ctc-audit-branch {
    margin-right: 8px;
    color: #8F8F8F;
}

.ctc-audit-amount {
    font-weight: 600;
    color: #01524B;
    white-space: nowrap;
}

.ctc-audit-level {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 22px;
    padding: 0 8px;
    border-radius: 3px;
    background: #F5F5F5;
    border: 0.82px solid #126D66;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    color: #01524B;
}

/* Rules that have no counterpart on the other side of the upload. */
.ctc-audit-flag {
    display: inline-flex;
    align-items: center;
    height: 22px;
    margin-left: 8px;
    padding: 0 8.24px;
    border-radius: 15px;
    font-size: 10px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: -0.02em;
    white-space: nowrap;
}

.ctc-audit-flag-removed {
    background: #FFF3E1;
    border: 0.82px solid #E2C3B5;
    color: #9D5617;
}

.ctc-audit-flag-added {
    background: #E1F2FF;
    border: 0.82px solid #B5CBE2;
    color: #0060C9;
}

.ctc-audit-emp-name {
    font-weight: 500;
    color: #34444C;
}

.ctc-audit-emp-code {
    font-size: 13px;
    line-height: 20px;
    color: #8F8F8F;
}

/* ── Employee map toolbar ───────────────────────────────────────────────── */
.ctc-audit-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
}

.ctc-audit-caption {
    font-weight: 500;
    font-size: 15px;
    line-height: 22px;
    letter-spacing: -0.02em;
    color: #727272;
}

/* The search box itself is the shared `.search-input-group` from
   resources/sass/_ctc.scss — only the frame's smaller in-modal geometry and its
   green edge are set here, so the two definitions cannot drift apart. */
.ctc-audit-search.search-input-group {
    width: 244px;
    height: 32px;
    border-color: #126D66 !important;
    border-radius: 8px !important;
    padding: 0 15px 0 0;
}

.ctc-audit-search.search-input-group .input-group-text {
    padding: 0 0 0 15px !important;
}

.ctc-audit-search.search-input-group .form-control {
    padding: 0 0 0 15px;
    font-size: 13px;
    font-weight: 400;
}

/* ── Empty states ───────────────────────────────────────────────────────── */
.ctc-audit-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 70px;
    padding: 25px 0;
    background: #F2F9F6;
    border: 1px solid #BDDBCE;
    border-radius: 4px;
    font-size: 13px;
    line-height: 20px;
    letter-spacing: -0.02em;
    text-align: center;
    color: #727272;
}

.ctc-audit-empty-cell {
    text-align: center;
    color: #727272 !important;
}

/* ── Footer ─────────────────────────────────────────────────────────────── */
.ctc-audit-footer {
    display: flex;
    justify-content: center;
}

/* Same palette as "Add New Cost" in the Employee Cost Settings header — the
   global .btn-secondary colours ($admin-primary-btn #004242, hover to the pale
   #DDF5F9 fill with the #0892A4 edge), restated here as they are on
   .btn-submit-cost in Configuration.vue because this is a raw <button>, not a
   b-button, so it never picks the system rules up. */
.ctc-audit-close-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 195px;
    height: 48px;
    padding: 10px 30px;
    background-color: #004242 !important;
    border: 1.5px solid #004242 !important;
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 19px;
    letter-spacing: -0.04em;
    text-transform: capitalize;
    color: #FFFFFF !important;
    transition: all 0.2s;
}

.ctc-audit-close-btn:hover {
    background-color: #DDF5F9 !important;
    border-color: #0892A4 !important;
    color: #004242 !important;
}

/* ---------------------------------------------------------------------------
   Audit list card (unchanged by the snapshot redesign).
   --------------------------------------------------------------------------- */
.ctc-rounded-lg {
    border-radius: 8px;
}

/* The search box (.search-input-group) is styled centrally in
   resources/sass/_ctc.scss so every CTC list screen shares one definition. */

/* The last column is the row action. DemoList prints it as plain cell text, so
   the capsule the design asks for is drawn on that text — the whole cell already
   carries DemoList's click handler, which opens the snapshot. */
.ctc-audit-list-wrap ::v-deep .employee-list-table tbody td:last-child {
    text-align: center;
}
.ctc-audit-list-wrap ::v-deep .employee-list-table tbody td:last-child > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 5px 16px;
    border: 1px solid #d7dde2;
    border-radius: 999px;
    background-color: #fff;
    color: #025147;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
    transition: all 0.2s ease;
}
.ctc-audit-list-wrap ::v-deep .employee-list-table tbody td:last-child > span:hover {
    background-color: #f0f6f5;
    border-color: #025147;
}

/* The <common-list> table itself — header bar, sort arrows (including the ones
   suppressed on this action column), body rows, zebra striping and the hidden
   column picker / filter trigger — is styled in resources/sass/_ctc.scss under
   `.ctc-audit-list-wrap`, shared with the Employees and Annual Reports lists so
   the three render one identical table. */
</style>

<!--
  Unscoped on purpose: BootstrapVue teleports the modal to document.body and
  builds .modal-dialog / .modal-content / .modal-body itself, so the scope
  attribute never reaches them and ::v-deep cannot match them either. Every rule
  below is namespaced by the modal-class instead.
-->
<style>
/* The snapshot frame is 987px wide and draws its own padding, title and close
   glyph, so the modal contributes nothing but the sheet. */
.ctc-audit-modal .modal-dialog {
    max-width: 987px;
    width: calc(100vw - 40px);
}

.ctc-audit-modal .modal-content {
    border: none !important;
    border-radius: 16px !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
}

.ctc-audit-modal .modal-body {
    padding: 0 !important;
}
</style>
